import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { IntercomProvider } from "../../components/IntercomMessenger";
import MessagePage from "../../components/MessagePage";
import useToken from "../../hooks/useToken";
import { startSessionReplay } from "../../lib/mixpanel";
import CampaignPage from "../CampaignPage";
import InvitationsQuestionnairePage from "../InvitationsQuestionnairePage";
import { FeedbackOverviewPage } from "../PerformanceEvaluation/FeedbackOverviewPage";
import PreviewPage from "../PreviewPage";
import QuestionnairePage from "../QuestionnairePage";
import { OverviewPage } from "../RequestForInformation/Overview";
import { StartNowPage } from "../RequestForInformation/StartNow";

import { basePath } from "./constants";
import { LocaleResolver } from "./LocaleResolver";
import { MockDevIndex, MockDevNav } from "./MockDevIndex";

const devIndexEnabled = import.meta.env.VITE_ENABLE_MSW === "true" || import.meta.env.MODE === "proxy";

/**
 * New version of the assessment forms app, shown if the appropriate feature toggle is toggled.
 *
 * Once the backend has been fully migrated from the old Django API to the assessments microservice:
 * - Remove the feature-toggle-wrapping `ApplicationPage` below
 * - Rename `NewApplicationPage` -> `ApplicationPage` and export this instead
 */
export const ApplicationPage: React.FC = () => {
    const { token, decoded } = useToken();

    React.useEffect(() => {
        if (decoded.tenant) {
            startSessionReplay();
        }
    }, [decoded.tenant]);

    const url =
        decoded.assessmentIds && decoded.assessmentIds.length > 0
            ? `${basePath}/assessments?token=${token}`
            : `${basePath}/?token=${token}`;

    const singleAssessmentId = decoded.assessmentIds?.length === 1 ? decoded.assessmentIds[0] : undefined;

    const startNowElement = singleAssessmentId ? (
        <Navigate to={`${basePath}/${singleAssessmentId}?token=${token}`} replace />
    ) : (
        <StartNowPage />
    );

    return (
        <>
            <LocaleResolver />
            <IntercomProvider>
                {devIndexEnabled && <MockDevNav />}
                <Routes>
                    {devIndexEnabled && <Route path="/" element={<MockDevIndex />} />}
                    <Route path="/preview" element={<PreviewPage />} />
                    <Route path={basePath + "/questionnaire"} element={<InvitationsQuestionnairePage />} />
                    <Route path={basePath} element={<CampaignPage />} />
                    <Route path={basePath + "/feedback"} element={<FeedbackOverviewPage />} />
                    <Route path={basePath + "/assessments"} element={startNowElement} />
                    <Route path={`${basePath}/assessments/overview`} element={<OverviewPage />} />
                    <Route path={`${basePath}/campaign/:campaignId/`} element={<CampaignPage />} />
                    <Route
                        path={`${basePath}/submitted`}
                        element={<MessagePage reason="completedAssessment" url={url} />}
                    />

                    <Route path={`${basePath}/:id/`} element={<QuestionnairePage />} />
                    <Route path={`${basePath}/*`} element={<MessagePage reason="404" />} />
                    <Route path="*" element={<MessagePage reason="404" />} />
                    <Route path="/" element={<MessagePage reason="404" />} />
                </Routes>
            </IntercomProvider>
        </>
    );
};
